import { getFirestore, getFirestoreDeleteField } from '../../system/firebase';

import { SNACK_BAR_MESSAGE_OPERATION_FAILED, SNACK_BAR_MESSAGE_SAVE, SNACK_BAR_MESSAGE_DELETE, USER_TYPE_JS, SNACK_BAR_MESSAGE_SAVED_HIRED_SUCCESS } from '../constants';
import { convertFirebaseTimestampsToDate } from '../dateHelper';
import { mapDefinedFields, mapJobHiredCandidateFromFirestore, removeFromDto } from '../dtoHelper';
import { ELASTICSEARCH_PAGE_SIZE, mapEmpJobsDataFromElasticSearchJson, mapProfilesDataFromElasticSearchJson } from '../elasticSearchHelper';
import { checkEsToken, elasticSearchEmpJobSearchPostFetch, getElasticSearchJsNameOccupationSuggestionPostFetch } from './appServices';
import { fetchProfilesByUserIdList } from './dashboardServices';

import _get from 'lodash/get';
import _set from 'lodash/set';
import _fpSet from 'lodash/fp/set';
import _keys from 'lodash/keys';
import _forEach from 'lodash/forEach';
import _orderBy from 'lodash/orderBy';
import _map from 'lodash/map';
import _omit from 'lodash/omit';
import _pick from 'lodash/pick';
import _isNil from 'lodash/isNil';

export const loadCountriesList = setCountriesList => {
    const db = getFirestore();
    const countriesRef = db.collection('app');
    return countriesRef.onSnapshot(docSnapshot => {
        setCountriesList(docSnapshot.docs.map(doc => doc.data())[0].countryList);
    });
};

export const loadStatesList = async (setStatesList, country) => {
    const db = getFirestore();

    const statesRef = db.doc(`app/lovs/countries/${country}`);

    const doc = await statesRef.get();
    doc.exists && setStatesList(doc.data().stateList.map(s => s.name));
};

export const loadCitiesList = async (setCitiesList, country, province) => {
    const db = getFirestore();
    if (Boolean(country) && Boolean(province)) {
        const citiesRef = db.doc(`app/lovs/countries/${country}/states/${province}`);
        const doc = await citiesRef.get();
        doc.exists && setCitiesList(doc.data().cityList.map(s => s.name));
    }
};

export const loadCityLocation = async (setLoc, country, province, city) => {
    const db = getFirestore();

    const citiesRef = db.doc(`app/lovs/countries/${country}/states/${province}`);

    const doc = await citiesRef.get();
    doc.exists && setLoc(doc.data().cityList?.filter(c => c.name === city)[0]?.location);
};

export const saveNewJob = async (data, employerId, employerData, setSnackbarMessage, setSnackbarErrorMessage, benefitSummary, companyBio) => {
    const db = getFirestore();
    const now = new Date();

    //-------------------------------------------------------------------------------------------
    // prep for jobs
    //-------------------------------------------------------------------------------------------
    const { active } = data;
    let payload = {
        ...data,
        employerId,
        ...employerData,
        benefitSummary,
        companyBio,
        createDt: now,
        statusDt: now,
        updateDt: now,
    };
    // if active, include more data
    if (active) {
        payload = { ...payload, activeDt: now };
    }

    const jobRef = db.collection('jobs');
    try {
        //-------------------------------------------------------------------------------------------
        // write jobs
        //-------------------------------------------------------------------------------------------
        const jobDocRef = await jobRef.add(mapDefinedFields(payload));

        //-------------------------------------------------------------------------------------------
        // save successfully
        //-------------------------------------------------------------------------------------------
        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);

        return { ...payload, uid: jobDocRef.id };
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return null;
    }
};

export const updateJob = async ({
    data,
    companyName,
    companyProfilePicUrl,
    benefitSummary,
    companyBio,
    isEmpBadge,
    activeOrig,
    setSnackbarMessage,
    setSnackbarErrorMessage,
    cleanUpAndClose,
}) => {
    const db = getFirestore();
    const deleteField = getFirestoreDeleteField();
    const now = new Date();

    //-------------------------------------------------------------------------------------------
    // prep for jobs
    //-------------------------------------------------------------------------------------------
    const { active, uid: jobId } = data;
    const jobRef = db.collection('jobs').doc(jobId);
    let jobDto = removeFromDto(data, ['uid']);

    let fieldsToBeDeleted = {};
    if (!activeOrig && active) {
        // if setting to active, renew activeDt and statusDt
        jobDto = _fpSet('activeDt', now, jobDto);
        jobDto = _fpSet('statusDt', now, jobDto);
        // if setting to active, put the employer fields back (use the fields that passed in)
        if (companyName) jobDto = _fpSet('companyName', companyName, jobDto);
        if (companyProfilePicUrl) jobDto = _fpSet('companyProfilePicUrl', companyProfilePicUrl, jobDto);
        if (benefitSummary) jobDto = _fpSet('benefitSummary', benefitSummary, jobDto);
        if (companyBio) jobDto = _fpSet('companyBio', companyBio, jobDto);
        if (!_isNil(isEmpBadge)) jobDto = _fpSet('isEmpBadge', isEmpBadge, jobDto); ////
    } else if (activeOrig && !active) {
        // if setting to active, set inactiveDt and statusDt
        jobDto = _fpSet('inactiveDt', now, jobDto);
        jobDto = _fpSet('statusDt', now, jobDto);
        // if setting to inactive, delete the employer fields from db
        fieldsToBeDeleted = { companyName: deleteField, companyProfilePicUrl: deleteField, benefitSummary: deleteField, companyBio: deleteField };
    }

    jobDto = { ...mapDefinedFields(jobDto), ...fieldsToBeDeleted, updateDt: new Date() };

    try {
        //-------------------------------------------------------------------------------------------
        // write jobs
        //-------------------------------------------------------------------------------------------
        await jobRef.update(jobDto);

        //-------------------------------------------------------------------------------------------
        // save successfully
        //-------------------------------------------------------------------------------------------
        cleanUpAndClose && cleanUpAndClose();
        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);

        return { ...jobDto, uid: jobId };
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return null;
    }
};

export const deleteJob = async (uid, setSnackbarMessage, setSnackbarErrorMessage) => {
    const db = getFirestore();

    const jobRef = db.collection('jobs').doc(uid);
    try {
        await jobRef.delete();
        setSnackbarMessage(SNACK_BAR_MESSAGE_DELETE);
        return true;
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return false;
    }
};

export const fetchHiredCandidates = async ({ jobId }) => {
    const db = getFirestore();

    // fetch the job seeker user Id list
    const jobHiredCandidateRef = db.collection('jobHiredCandidates').doc(jobId);
    let hiredCandidates = null;

    try {
        const doc = await jobHiredCandidateRef.get();
        if (doc.exists) {
            const jsIdList = _get(doc.data(), 'jsIdList', []);
            if (jsIdList.length > 0) {
                // fetch the profiles of all the user id's in the list
                const profiles = await fetchProfilesByUserIdList({ userIdList: jsIdList });
                hiredCandidates = _map(profiles, profile =>
                    mapJobHiredCandidateFromFirestore({ dto: _pick(profile, ['uid', 'firstName', 'middleName', 'lastName', 'profilePicUrl', 'country', 'state', 'city']) }),
                );
            }
        }
    } catch (error) {
        console.log('fetchHiredCandidates failed: ', error);
    }

    return hiredCandidates;
};

export const updateHiredCandidates = async ({ jsIdList = [], jobId, setSnackbarMessage, setSnackbarErrorMessage, callback }) => {
    const db = getFirestore();
    const now = new Date();

    const jobHiredCandidateRef = db.collection('jobHiredCandidates').doc(jobId);

    const dto = { jsIdList, createDt: now, updateDt: now };

    try {
        // Now that the alerts have been set, reset the alert flags in the database to false.
        await db.runTransaction(async transaction => {
            await transaction.set(jobHiredCandidateRef, dto, { merge: true });

            console.log('updateHiredCandidates Transaction successfully committed!');
        });

        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVED_HIRED_SUCCESS);
        callback && callback();

        return dto;
    } catch (error) {
        console.log('updateHiredCandidates Transaction failed: ', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
        return null;
    }
};

export const fetchEmpJobs = async ({ from = 0, size = ELASTICSEARCH_PAGE_SIZE, queryObject, activeView = true, esToken, setEsToken } = {}) => {
    // the other view (not visible) always start from page 1
    const activeFrom = activeView ? from : 0;
    const inactiveFrom = activeView ? 0 : from;

    // format the search criteria queryObject
    const sortDto = queryObject?.sortTag || undefined;

    // Create the actual queryObject for ElasticSearch (active jobs)
    const mappedQueryObjectActive = mapDefinedFields({
        parentId: queryObject?.parentId || undefined,
        jobTitle: queryObject?.searchText || undefined,
        active: true,
        ...sortDto,
    });
    // console.log('==========> ', { mappedQueryObjectActive });

    // Create the actual queryObject for ElasticSearch (inactive jobs)
    const mappedQueryObjectInactive = mapDefinedFields({
        parentId: queryObject?.parentId || undefined,
        jobTitle: queryObject?.searchText || undefined,
        active: false,
        ...sortDto,
    });
    // console.log('==========> ', { mappedQueryObjectInactive });

    let checkedEsToken = null;
    try {
        checkedEsToken = await checkEsToken(esToken);

        if (checkedEsToken) {
            const searchResult = {};

            const searchResultActive = await elasticSearchEmpJobSearchPostFetch({
                queryObject: { ...mappedQueryObjectActive, from: activeFrom, size },
                token: checkedEsToken.token,
            });

            if (_get(searchResultActive, 'success')) {
                const total = _get(searchResultActive, 'total', 0);
                const result = _get(searchResultActive, 'result', []);
                const mappedList = _map(result, empItem => {
                    return mapEmpJobsDataFromElasticSearchJson(empItem);
                });
                _set(searchResult, 'activeJobs', { total, list: mappedList });
            } else {
                console.error('Failed to search employer my jobs active jobs');
            }

            const searchResultInactive = await elasticSearchEmpJobSearchPostFetch({
                queryObject: { ...mappedQueryObjectInactive, from: inactiveFrom, size },
                token: checkedEsToken.token,
            });

            if (_get(searchResultInactive, 'success')) {
                const total = _get(searchResultInactive, 'total', 0);
                const result = _get(searchResultInactive, 'result', []);
                const mappedList = _map(result, empItem => {
                    return mapEmpJobsDataFromElasticSearchJson(empItem);
                });
                _set(searchResult, 'inactiveJobs', { total, list: mappedList });
            } else {
                console.error('Failed to search employer my jobs inactive jobs');
            }

            if (checkedEsToken.isNew) {
                setEsToken(_omit(checkedEsToken, 'isNew'));
            }

            return searchResult;
        }
    } catch (error) {
        console.error('Failed checking token', error);
    }

    return { total: 0, list: [] };
};

export const loadMyJobsListByEmployerId = (employerId, set) => {
    const db = getFirestore();
    const empJobsRef = db.collection('myJobsEmp').doc(employerId).collection('empJobs');
    return empJobsRef.onSnapshot(docSnapshot => {
        let jobList = [];
        docSnapshot.docs.forEach(doc => {
            const jobId = doc.id;
            const empJobData = doc.data();

            let jobSeekers = _get(empJobData, 'jobSeekers');
            const jobSeekerIds = _keys(jobSeekers);
            _forEach(jobSeekerIds, jobSeekerId => {
                jobSeekers = _fpSet(jobSeekerId, convertFirebaseTimestampsToDate(jobSeekers[jobSeekerId], ['applyDt', 'accountDeleteDt', 'savedDt']), jobSeekers);
            });

            jobList.push(convertFirebaseTimestampsToDate({ ...empJobData, uid: jobId, jobSeekers }, ['activeDt', 'inactiveDt', 'applyDt', 'statusDt', 'createDt', 'updateDt']));
        });

        jobList = _orderBy(jobList, ['createDt'], ['desc']);
        set(jobList);
    });
};

export const loadActiveJobsListByEmployerId = (employerId, set) => {
    const db = getFirestore();
    const empJobsRef = db.collection('myJobsEmp').doc(employerId).collection('empJobs').where('active', '==', true);
    return empJobsRef.onSnapshot(docSnapshot => {
        let jobList = [];
        docSnapshot.docs.forEach(doc => {
            const jobId = doc.id;
            const empJobData = doc.data();

            let jobSeekers = _get(empJobData, 'jobSeekers');
            const jobSeekerIds = _keys(jobSeekers);
            _forEach(jobSeekerIds, jobSeekerId => {
                jobSeekers = _fpSet(jobSeekerId, convertFirebaseTimestampsToDate(jobSeekers[jobSeekerId], ['applyDt', 'accountDeleteDt', 'savedDt']), jobSeekers);
            });

            jobList.push(convertFirebaseTimestampsToDate({ ...empJobData, uid: jobId, jobSeekers }, ['activeDt', 'inactiveDt', 'applyDt', 'statusDt', 'createDt', 'updateDt']));
        });

        jobList = _orderBy(jobList, ['createDt'], ['desc']);
        set(jobList);
    });
};

export const fetchJob = async (jobId, set, setSnackbarErrorMessage) => {
    const db = getFirestore();
    const jobRef = db.collection('jobs').doc(jobId);

    try {
        const doc = await jobRef.get();
        set({ ...convertFirebaseTimestampsToDate({ ...doc.data(), uid: doc.id }, ['activeDt', 'inactiveDt', 'applyDt', 'statusDt', 'createDt', 'updateDt']) });
    } catch (error) {
        console.log('Failed to retrieve job record', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};

export const fetchAllUsers = set => {
    const db = getFirestore();
    const userRef = db.collection('users').where('type', '==', USER_TYPE_JS);

    return userRef.onSnapshot(docSnapshot => {
        set(docSnapshot.docs.map(doc => doc.data()));
    });
};

export const clearUnreadNewApplicants = ({ employerId, jobId }) => {
    const db = getFirestore();
    const dashboardRef = db.collection('dashboards').doc(employerId);
    const dashboardDto = { [`unreadNewApplicants.${jobId}`]: getFirestoreDeleteField() };
    dashboardRef.update(dashboardDto);
};

//--------------------------------------------------------------------------
// Job Seekers Suggestion
//--------------------------------------------------------------------------
export const getJobSeekersSuggestions = async ({ keywords, esToken, setEsToken }) => {
    // const db = getFirestore();

    let checkedEsToken = null;
    try {
        checkedEsToken = await checkEsToken(esToken);
    } catch (error) {
        console.log('Failed checking token', error);
    }

    try {
        if (checkedEsToken) {
            const esResp = await getElasticSearchJsNameOccupationSuggestionPostFetch({
                // url: ELASTICSEARCH_JS_NAME_OCCUPATION_SUGGESTION_API_URL,
                // type: ELASTICSEARCH_TYPE_JS_PROFILE,
                queryObject: mapDefinedFields({ firstName: keywords, middleName: keywords, lastName: keywords }),
                token: checkedEsToken.token,
            });

            if (checkedEsToken.isNew) {
                setEsToken(_omit(checkedEsToken, 'isNew'));
            }

            const result = _get(esResp, 'result', []);
            // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> ', { result });
            const mappedResult = _map(result, item => mapProfilesDataFromElasticSearchJson(item));

            return mappedResult;
        }

        return [];
    } catch (error) {
        console.log('Transaction failed: ', error);
    }
};
